import { useEffect, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { adminService } from '../services/admin-service';
import { Loader2 } from 'lucide-react';

interface DailyStats {
  date: string;
  dateFormatted: string;
  tenants: number;
  users: number;
  messages: number;
}

export function AdminAnalytics() {
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDailyStats();
  }, []);

  const loadDailyStats = async () => {
    try {
      setIsLoading(true);
      const data = await adminService.getDailyStats();
      setDailyStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas diárias:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Carregando análises...</span>
      </div>
    );
  }

  // Calcular totais
  const totalTenants = dailyStats.reduce((sum, day) => sum + day.tenants, 0);
  const totalUsers = dailyStats.reduce((sum, day) => sum + day.users, 0);
  const totalMessages = dailyStats.reduce((sum, day) => sum + day.messages, 0);

  const avgTenants = Math.round(totalTenants / dailyStats.length);
  const avgUsers = Math.round(totalUsers / dailyStats.length);
  const avgMessages = Math.round(totalMessages / dailyStats.length);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Análises - Últimos 30 Dias</h2>
        <button
          onClick={loadDailyStats}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Atualizar Dados
        </button>
      </div>

      {/* Cards com médias */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Média Diária - Tenants</p>
              <p className="text-3xl font-bold text-blue-600">{avgTenants}</p>
              <p className="text-xs text-gray-500 mt-1">tenants ativos/dia</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-2xl">🏢</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Média Diária - Usuários</p>
              <p className="text-3xl font-bold text-green-600">{avgUsers}</p>
              <p className="text-xs text-gray-500 mt-1">usuários ativos/dia</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <span className="text-2xl">👥</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Média Diária - Mensagens</p>
              <p className="text-3xl font-bold text-purple-600">{avgMessages}</p>
              <p className="text-xs text-gray-500 mt-1">mensagens/dia</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <span className="text-2xl">💬</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico de Mensagens */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mensagens Trocadas por Dia</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dailyStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="dateFormatted" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
              labelStyle={{ color: '#333' }}
            />
            <Bar dataKey="messages" fill="#8b5cf6" name="Mensagens" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Gráfico de Tenants e Usuários */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tenants e Usuários Ativos por Dia</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dailyStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="dateFormatted" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
              labelStyle={{ color: '#333' }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="tenants" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Tenants Ativos"
              dot={{ fill: '#3b82f6' }}
            />
            <Line 
              type="monotone" 
              dataKey="users" 
              stroke="#10b981" 
              strokeWidth={2}
              name="Usuários Ativos"
              dot={{ fill: '#10b981' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Gráfico Combinado */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Visão Geral - Todas as Métricas</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={dailyStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="dateFormatted" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
              labelStyle={{ color: '#333' }}
            />
            <Legend />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="tenants" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Tenants"
              dot={{ fill: '#3b82f6' }}
            />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="users" 
              stroke="#10b981" 
              strokeWidth={2}
              name="Usuários"
              dot={{ fill: '#10b981' }}
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="messages" 
              stroke="#8b5cf6" 
              strokeWidth={2}
              name="Mensagens"
              dot={{ fill: '#8b5cf6' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

